import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  applyActionCode, 
  confirmPasswordReset, 
  checkActionCode 
} from "firebase/auth";
import { auth } from "../firebase/config";
import { useToast } from "../context/ToastContext";
import { 
  ShieldCheck, 
  ShieldAlert, 
  ShieldX, 
  Lock, 
  RefreshCcw, 
  ArrowRight,
  Activity,
  UserCheck
} from "lucide-react";
import "./VerifyEmail.css"; // Reuse clinical HUD styles

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlert } = useToast();
  const processed = React.useRef(false);
  
  const [status, setStatus] = useState("initializing"); // initializing, processing, success, error
  const [message, setMessage] = useState("Executing Clinical Identity Check...");
  const [newPassword, setNewPassword] = useState("");
  
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    // Sovereign Guard: Ensure authorization sequence only executes once
    if (processed.current) return;
    
    if (!mode || !oobCode) {
      setStatus("error");
      setMessage("INVALID_AUTHORIZATION_LINK: Security breach detected or link expired.");
      return;
    }

    processed.current = true;

    switch (mode) {
      case "verifyEmail":
        handleVerifyEmail();
        break;
      case "resetPassword":
        handleCheckPasswordResetCode();
        break;
      case "recoverEmail":
        setStatus("error");
        setMessage("RECOVER_EMAIL_GATE: Please contact system administration for manual recovery.");
        break;
      default:
        setStatus("error");
        setMessage("UNKNOWN_SECURITY_MODE: Terminal logic mismatch.");
    }
  }, [mode, oobCode, navigate]);

  const handleVerifyEmail = async () => {
    setStatus("processing");
    setMessage("SYNCHRONIZING_IDENTITY_ASSETS...");
    try {
      await applyActionCode(auth, oobCode);
      setStatus("success");
      setMessage("IDENTITY_VERIFIED: Clinical authorization sequence completed successfully.");
      showAlert("Medical identity synchronized. Welcome to the Medico Ecosystem.", "success");
      
      // Auto-redirect after success
      setTimeout(() => navigate("/dashboard/patient"), 4000);
    } catch (error) {
      setStatus("error");
      setMessage(`VERIFICATION_FAILURE: ${error.message}`);
      showAlert("Authorization Breach detected during identity sync.", "error");
    }
  };

  const handleCheckPasswordResetCode = async () => {
    setStatus("processing");
    setMessage("VERIFYING_SECURITY_KEY...");
    try {
      await checkActionCode(auth, oobCode);
      setStatus("reset_form");
      setMessage("KEY_VERIFIED: Please initialize your new security credentials.");
    } catch (error) {
      setStatus("error");
      setMessage(`SECURITY_KEY_INVALID: ${error.message}`);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      showAlert("Security Threshold: Key must be at least 6 characters.", "error");
      return;
    }

    setStatus("processing");
    setMessage("ENCRYPTING_NEW_CREDENTIALS...");
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("success");
      setMessage("CREDENTIALS_RELOADED: Your new security key has been authorized.");
      showAlert("Security Key Updated. Access Portal Re-Enabled.", "success");
      setTimeout(() => navigate("/auth"), 3000);
    } catch (error) {
      setStatus("error");
      setMessage(`ENCRYPTION_FAILURE: ${error.message}`);
      showAlert("Credential update failure. Authorization breach.", "error");
    }
  };

  return (
    <div className="verify-container">
      <div className="verify-card">
        {/* Tactical HUD Corner Brackets */}
        <div className="hud-corner-tl"></div>
        <div className="hud-corner-br"></div>

        <div className="verify-logo-wrapper">
          {status === "success" && <ShieldCheck size={32} strokeWidth={2.5} style={{ color: "#10b981" }} />}
          {status === "error" && <ShieldX size={32} strokeWidth={2.5} style={{ color: "#ef4444" }} />}
          {(status === "processing" || status === "initializing") && <RefreshCcw size={32} className="spinning-icon" />}
          {status === "reset_form" && <Lock size={32} strokeWidth={2.5} />}
        </div>

        <h1>{mode === "verifyEmail" ? "Identity Auth" : "Credential Reset"}</h1>
        
        <div className="verify-status-hud">
          <div className={`status-row ${status === "success" ? "verified" : status === "error" ? "pending" : ""}`}>
            <Activity size={18} className={status === "processing" ? "pulse" : ""} />
            <span>{message}</span>
          </div>
        </div>

        {status === "reset_form" && (
          <form onSubmit={handlePasswordReset} className="verify-actions" style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.65rem', 
                fontFamily: 'Space Mono', 
                color: '#94a3b8',
                marginBottom: '0.5rem',
                letterSpacing: '2px'
              }}>NEW_SECURITY_KEY</label>
              <input 
                type="password" 
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••••••"
                style={{
                  width: '100%',
                  padding: '1.1rem',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  fontFamily: 'Space Mono'
                }}
                required
              />
            </div>
            <button className="verify-btn primary" type="submit">
              EXECUTE_RESET <ArrowRight size={18} />
            </button>
          </form>
        )}

        {status === "success" && (
          <div className="verify-actions">
            <button className="verify-btn primary" onClick={() => navigate("/")}>
              ENTER_ECOSYSTEM <UserCheck size={18} />
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="verify-actions">
            <button className="verify-btn secondary" onClick={() => navigate("/auth")}>
              RETRY_AUTHORIZATION <RefreshCcw size={18} />
            </button>
          </div>
        )}

        <div className="verify-footer">
          <p>
            <Activity size={12} className="pulse" />
            <span>SESSION_STATUS: {status.toUpperCase()} | HANDSHAKE_PROTOCOL: ALPHA_V2</span>
          </p>
        </div>
      </div>

      {status === "processing" && (
        <div className="verify-loading-overlay">
          <div className="loader-box">
            <RefreshCcw size={48} className="spinning-icon" />
            <p>EXECUTING_AUTHORIZATION_ACTION...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthAction;
